import { getConfig } from '@edx/frontend-platform';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

export const formatMinutes = (totalMinutes) => {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return null;
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

// i18n-aware variant: same shape as formatMinutes but resolves unit labels via
// react-intl so translations/locale digits apply. Pass the discover messages catalog.
export const formatDuration = (totalMinutes, intl, messages) => {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return null;
  if (totalMinutes < 60) {
    return intl.formatMessage(messages.durationMinutes, { minutes: totalMinutes });
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0
    ? intl.formatMessage(messages.durationHoursMinutes, { hours, minutes })
    : intl.formatMessage(messages.durationHours, { hours });
};

const pickRating = (contentId, ratingsByContentId) => {
  const real = ratingsByContentId?.[contentId];
  if (real && real.avgRating != null && real.reviewCount > 0) {
    return {
      rating: Number(Number(real.avgRating).toFixed(1)),
      reviewCount: real.reviewCount,
    };
  }
  return { rating: null, reviewCount: 0 };
};

export const parseDiscoverCourses = (
  courses,
  tagsByCourseId = {},
  ratingsByContentId = {},
  effortByCourseId = {},
  isNewByCourseId = {},
) => {
  if (!Array.isArray(courses)) return [];

  return courses
    .filter((course) => course?.details)
    .map((course) => {
      const details = course.details || {};
      const firstMode = course.modes?.[0];
      const courseId = course.openedxCourseId || course.id;
      const contentId = course.id;
      const graphqlTags = (course.courseTags || []).map((t) => t.tag).filter(Boolean);
      const taxonomyTags = tagsByCourseId[courseId] || [];
      const tags = taxonomyTags.length > 0 ? taxonomyTags : graphqlTags;
      const imageUrl = details.courseImageUrl
        ? `${getConfig().LMS_BASE_URL}${details.courseImageUrl}`
        : FALLBACK_IMAGE;
      const { rating, reviewCount } = pickRating(contentId, ratingsByContentId);
      const totalMinutes = effortByCourseId[courseId]?.total_minutes;
      const duration = formatMinutes(totalMinutes);

      return {
        id: courseId,
        contentId,
        title: details.displayName || 'Untitled course',
        imageUrl,
        type: 'Course',
        duration,
        rating,
        reviewCount,
        skills: tags,
        price: firstMode?.minPrice ?? 0,
        // Not self-service: the card shows how it's obtained instead of a price
        // (a subscription-only course has minPrice 0 and would read "Free").
        subscriptionOnly: Boolean(course.subscriptionOnly),
        inviteOnly: Boolean(course.inviteOnly),
        isNew: Boolean(isNewByCourseId[courseId]),
        createdAt: details.start || null,
      };
    });
};

const parseProgramDuration = (duration, metric) => {
  if (!duration) return null;
  const shortMetric = {
    hours: 'h',
    days: 'd',
    weeks: 'w',
    months: 'mo',
    years: 'y',
  }[String(metric || '').toLowerCase()] || metric || '';
  return `${duration}${shortMetric}`;
};

export const parseDiscoverPrograms = (programs, ratingsByContentId = {}) => {
  if (!Array.isArray(programs)) return [];

  return programs.map((program) => {
    const contentId = program.id;
    const tags = (program.programTags || []).map((t) => t.tag).filter(Boolean);
    const imageUrl = program.image
      ? (program.image.startsWith('http')
        ? program.image
        : `${getConfig().LMS_BASE_URL}${program.image}`)
      : FALLBACK_IMAGE;
    const { rating, reviewCount } = pickRating(contentId, ratingsByContentId);

    return {
      id: program.id,
      contentId,
      title: program.name || 'Untitled program',
      imageUrl,
      type: 'Program',
      duration: parseProgramDuration(program.duration, program.durationMetric),
      rating,
      reviewCount,
      skills: tags,
      price: program.price ?? 0,
      subscriptionOnly: Boolean(program.subscriptionOnly),
      inviteOnly: Boolean(program.inviteOnly),
      createdAt: program.createdAt || null,
    };
  });
};

export const parseDiscoverPathways = (pathways = []) => {
  if (!Array.isArray(pathways)) return [];

  return pathways.map((pathway) => {
    const imageUrl = pathway.image
      ? (pathway.image.startsWith('http')
        ? pathway.image
        : `${getConfig().LMS_BASE_URL}${pathway.image}`)
      : FALLBACK_IMAGE;

    return {
      id: pathway.id,
      contentId: pathway.id,
      title: pathway.name || 'Untitled learning path',
      imageUrl,
      type: 'Pathway',
      duration: parseProgramDuration(pathway.duration, pathway.durationMetric),
      rating: null,
      reviewCount: 0,
      skills: [],
      price: 0,
      createdAt: pathway.createdAt || null,
    };
  });
};
